import { ComponentBase } from '../../../component-base';
import { IMouseMove, IMouseRelease, IMouseDown } from '../Interfaces';
import { PositionCalculatorService } from '../position-calculator.service';
import { DrawingsHolderService } from '../drawings-holder.service';
import { PolyLine, ConnectionPoint, ScaleDot, Arc } from '../object3D';
import { Injectable } from '@angular/core';
import { common } from '../common';

declare let THREE: any;

interface Distance {
  point: ConnectionPoint;
  distanceFromOrigin: any;
}

interface ScaleInfo {
  scaleDirection: any;
  distances: Array<Distance>;
}

@Injectable()
export class PolyLineScaleComponent extends ComponentBase
  implements IMouseDown, IMouseMove, IMouseRelease {
  private raycaster = new THREE.Raycaster(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -1, 0));
  private selectedObject: PolyLine | Arc | undefined;
  private lastMousePosition = new THREE.Vector3();
  private sclaInfo: Array<ScaleInfo>;
  private polyLineSize = new THREE.Vector3();
  private dragDistance = new THREE.Vector3();
  private dragDirection = new THREE.Vector3();
  private oldSize = new THREE.Vector3();
  private newSizeOffset = new THREE.Vector3();
  private pointOffset = new THREE.Vector3();
  private scaleDot: ScaleDot | null = null;

  constructor(
    private positionCalculator: PositionCalculatorService,
    private drawings: DrawingsHolderService
  ) {
    super();
    this.disable();
    this.drawings.onSelect.subscribe(obj => {
      if (obj) {
        this.selectedObject = obj[0] instanceof PolyLine || obj[0] instanceof Arc ? obj[0] : null;
      } else {
        this.selectedObject = null;
      }
    });
  }

  mouseDown(e: MouseEvent): boolean {
    if (this.selectedObject) {
      const position = this.positionCalculator.calculate(e);
      this.raycaster.ray.origin.copy(position).setY(common.raycastAtY);
      const dots = this.selectedObject.scaler.getDots();
      const intersection = this.raycaster.intersectObjects(dots) as Array<any>;
      if (intersection.length !== 0) {
        this.scaleDot = intersection[0].object as ScaleDot;
        const list = this.selectedObject.objectsList();
        const scaleDirections = this.scaleDot.directions;
        const polylineBoundingBox = new THREE.Box3().setFromPoints(
          list.map(i => i.currentPoint.position.clone())
        );
        const center = new THREE.Vector3();
        polylineBoundingBox.getCenter(center);
        polylineBoundingBox.getSize(this.polyLineSize);

        this.sclaInfo = scaleDirections.map(scaleDirection => {
          const offset = scaleDirection
            .clone()
            .negate()
            .multiply(this.polyLineSize.clone().divideScalar(2));
          const origin = center.clone().add(offset);
          return {
            scaleDirection,
            distances: list.map(item => ({
              point: item.currentPoint,
              distanceFromOrigin: item.currentPoint.position
                .clone()
                .sub(origin)
                .projectOnVector(scaleDirection)
                .length()
            }))
          };
        });

        this.lastMousePosition.copy(position);
        return true;
      }
    }
    return false;
  }

  mouseMove(e: MouseEvent): boolean {
    if (this.scaleDot) {
      const currentMousePosition = this.positionCalculator.calculate(e);
      const resultingScaleDirection = this.scaleDot.direction;

      const dragDistance = this.dragDistance
        .copy(currentMousePosition)
        .sub(this.lastMousePosition)
        .projectOnVector(resultingScaleDirection);

      this.sclaInfo.forEach(scaleInfo => {
        this.updatePointsPositions(
          currentMousePosition,
          scaleInfo,
          dragDistance,
          resultingScaleDirection
        );
      });
      if (this.selectedObject instanceof PolyLine) {
        this.selectedObject.updateConnection();
      } else {
        this.selectedObject.update();
        this.selectedObject.scaler.update();
      }
      this.lastMousePosition.copy(currentMousePosition);
      return true;
    }
    return false;
  }

  private updatePointsPositions(
    currentMousePosition,
    scaleInfo: ScaleInfo,
    dragDistance,
    resultingScaleDirection
  ) {
    const { scaleDirection, distances } = scaleInfo;

    const dragDirection = this.dragDirection
      .copy(currentMousePosition)
      .sub(this.lastMousePosition)
      .projectOnVector(resultingScaleDirection)
      .projectOnVector(scaleDirection)
      .normalize();

    const dragLength = dragDistance.clone().projectOnVector(dragDirection).length();

    if (isNaN(dragLength)) {
      return;
    }

    const oldSize = this.oldSize.copy(this.polyLineSize).multiply(scaleDirection).length();
    const newSizeOffset = this.newSizeOffset
      .set(dragLength, 0, dragLength)
      .multiply(scaleDirection);
    newSizeOffset.set(Math.abs(newSizeOffset.x), 0, Math.abs(newSizeOffset.z));
    this.polyLineSize.add(newSizeOffset);
    const newSize = oldSize + newSizeOffset.length();
    const scaleDelta = newSize / oldSize;
    if (!isNaN(scaleDelta)) {
      distances.forEach(item => {
        const oldDistance = item.distanceFromOrigin;
        const newDistance = oldDistance * scaleDelta;
        const delta = newDistance - oldDistance;
        item.distanceFromOrigin = newDistance;
        this.pointOffset.copy(dragDirection).multiplyScalar(delta);
        item.point.position.add(this.pointOffset);
      });
    }
  }

  mouseUp(e: MouseEvent): boolean {
    if (this.scaleDot) {
      this.scaleDot = null;
      return true;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/proTools/components/PolyLineScaleComponent.ts